import { pdsI18n } from './pds-i18n';
import { pdsUtils } from './pds-utils';
import { PropertyObserver } from './models/property-observer';
export type StringBoolean = 'true' | 'false' | null;
import {Subscription} from 'rxjs';
export abstract class PdsWidget {
  is: string;

  pds: any;

  // event keycodes for event processing.
  escKey = 27;
  tabKey = 9;

  //classList is an HTML element property.
  classList: any;
  //styles is an HTML property
  style: any;
  //Our pattern for generating dynamic IDs is to use the name property and append it
  name: string;
  // inner html is an html property
  innerHTML: any;
  // offsetTop is an html property
  offsetTop: number;

  cloneNode: (flag: boolean) => any;

  customStyle: any;

  selfToken: any;

  //This is how you tell Polymer that something needs to run through change detection.
  notifyPath: (value: string) => void;

  //This polymer method combines notifyPath with actually setting the value
  // https://polymer-library.polymer-project.org/1.0/docs/devguide/model-data#set-path
  // Polymer will not see deep changes unless this function or notifyPath is used.
  set: (path: string, value: any) => void;

  //This is a polymer shortcut to any elements with an ID in the component's template. $.elementId is the element with id=elementId
  // This does not include elements in dom-repeats, as there may be more than 1 matching element
  // https://polymer-library.polymer-project.org/1.0/docs/devguide/local-dom#node-finding
  $: { [elementId: string]: HTMLElement; };

  // Polymer built in function to fire events
  fire: (string, any) => {};

  //Polymer built in function to get content children
  getContentChildren: (selector: string) => any;

  //Polymer built in function to get effective children
  // https://polymer-library.polymer-project.org/1.0/docs/devguide/local-dom#effective-children
  getEffectiveChildren: (selector?: string) => Array<HTMLElement>;

  addEventListener: (string, any) => any;

  // Polymer built in function to debounce
  debounce: (string, Function, number) => any;

  redrawTrigger: boolean;

  firstChild: Node | null;
  lastChild: Node | null;

  children: HTMLCollection;

  childNodes: NodeList;

  // polymer array methods
  //  https://polymer-library.polymer-project.org/1.0/docs/devguide/model-data#array-mutation
  push: (path: string, ...items: Array<any>) => number;
  pop: (path: string) => any;
  unshift: (path: string, ...items: Array<any>) => number;
  shift: (path: string) => any;
  splice: (path: string, index: number, removeCount: number, ...items: Array<any>) => Array<any>;
  notifySplices: (path: string, items: Array<{ index: number, removed: Array<any>, addedCount: number, object: Array<any>, type: 'splice' }>) => number;

  protected subscriptions: Array<Subscription> = [];

  //Since most widgets aren't going to use watches, we default this to undefined.
  //It will get defined when someone watches a property.
  private observeredPaths: Record<string, PropertyObserver> | undefined;

  // shortcut to this element typecasted as an element
  asElement(): HTMLElement {
    return this as unknown as HTMLElement;
  }

  // shortcut to Polymer's DOM API - used so that Polymer can detect DOM manipulations
  //  https://polymer-library.polymer-project.org/1.0/docs/devguide/local-dom#dom-api
  //  defaults to this as an element as that is a common use case
  polymerDom(element: HTMLElement = this.asElement()): any {
    return (window as any).Polymer.dom(element);
  }

  // shortcut to Polymer's DOM API flush method - used so execute async operations
  //  https://polymer-library.polymer-project.org/1.0/docs/devguide/local-dom#dom-api
  flushPolymer(): void {
    (window as any).Polymer.dom.flush();
  }

  /**
   * This will be called on all of the elements that are in the DOM when the message keys get updated.
   */
  messageKeysUpdated(): void {
    setTimeout(() => {
      this.redrawTrigger = !this.redrawTrigger;
    });
  }

  //this is a polymer hook
  ready(): void {
    this.redrawTrigger = true;
  }

  attached(): void {
    this.classList.add('pds-widget');
    this.setAttribute('pds-widget', 'pds-widget');
  }

  detached(): void {
    if (this.observeredPaths != null) {
      // disable each property observer
      for (let key of Object.keys(this.observeredPaths)) {
        this.observeredPaths[key].disable();
      }

      // reset the observered paths
      this.observeredPaths = {};
    }
  }

  /* The following are functions that get attached as DOM element functions. */
  querySelector(param: string): any {
    //to be overridden by an overriding element.
    console.error('this should never have been called');
    //NOTE: It should be deleted in pdsMain and then inherited from the DOM element.
  }

  /* The following are functions that get attached as DOM element functions. */
  querySelectorAll(param: string): any {
    //to be overridden by an overriding element.
    console.error('this should never have been called');
    //NOTE: It should be deleted in pdsMain and then inherited from the DOM element.
  }

  dispatchEvent(e: Event): void {
    //to be overridden by an overriding element.
    console.error('this should never have been called');
    //NOTE: It should be deleted in pdsMain and then inherited from the DOM element.
  }

  hasAttribute(name: string): boolean {
    console.error('this should never have been called');
    return false;
  }

  setAttribute(attr: string, value: string): void {
    console.error('this should never have been called');
  }

  appendChild(node: Node): Node {
    console.error('this should never have been called');
    return node;
  }

  verifyNamePresent(): void {
    if (this.name == null) {
      console.error(this.is + ' component is missing the name attribute.');
    }
  }

  /* The following are functions made to make Polymer template syntax suck less*/
  logicalAnd(...value: Array<boolean | undefined>): boolean {

    let answer = true === value.reduce((v1: boolean | undefined, v2: boolean | undefined) => {
      return v1 && v2;
    });
    //console.log('Anding: ' + value[0] + ' ' + value[1] + ' answer: ' + answer);
    return answer;
  }

  /**
   * sub for updateStyles();
   * for some reason updateStyle as followed from the polymer docs wouldnt work, go figure.
   */

  updateStyles(styleName: string, styleValue: string): void {
    this.setAttribute('style', `${styleName}: ${styleValue}`);
  }

  /**
   * Created so we don't have to create so many getClass functions.
   * Examples:
   * {{conditionClass('pds-is-active', isShowing)}}
   * {{conditionClass('pds-is-active', user, '!= null')}}
   * {{conditionClass('pds-is-active', myAge, '<', yourAge)}}
   */
  conditionalClass(className: string, value1: any, operator?: string, value2?: any): string | undefined {
    if (operator == null) {
      if (value1) {
        return className;
      }
    } else if (operator.trim() === '!') {
      if (!value1) {
        return className;
      }
    } else if (this.compare(value1, operator, value2)) {
      return className;
    }
  }

  /**
   * this function is an alias of conditional class
   * bsed upon a condition and an attribute value
   * @param attributeValue
   */

  conditionalAttribute(attributeValue: string, value1: any, operator?: string, value2?: any): string | undefined {
    return this.conditionalClass(attributeValue, value1, operator, value2);
  }

  compare(value1: any, operator: string, value2?: any): boolean {
    switch (operator.trim()) {
      case 'is-blank':
        return value1 == null || value1.length === 0;
      case 'has-letters':
        //The idea here is "will the string show up in an element?"  So it has to be non-white space and not null.
        return value1 != null && /[^\s]/.test(value1);
      case '== null':
      case '==null':
        return value1 == null;
      case '!= null':
      case '!=null':
        return value1 != null;
      case '=== false':
      case '===false':
        return value1 === false;
      case '=== true':
      case '===true':
        return value1 === true;
      /* tslint:disable */
      case '==':
        return value1 == value2;
      case '!=':
        return value1 != value2;
      /* tslint:enable */
      case '===':
        return value1 === value2;
      case '!==':
        return value1 !== value2;
      case '<':
        return value1 < value2;
      case '<=':
        return value1 <= value2;
      case '>':
        return value1 > value2;
      case '>=':
        return value1 >= value2;
      case '!': //This the "not" case since you cannot run conditionalClass('myClass', !myVar)
        return !value1;
      default:
        throw 'operator not found';
    }
  }

  /**
   * compare doesn't work when you pass in undefined values.  This will let us check for undefined values
   * because the property name is passed in as a string.  However, this isn't going
   * to be called if the contents of the propertyName gets changed, only when the context changes.
   * @param propertyName
   * @param context
   * @returns {boolean} true if it's != null, false other wise.
   */
  isDefined(propertyName: string, context?: any): boolean {

    if (context == null) {
      context = this;
    }

    return context[propertyName] != null;
  }

  //redrawTrigger isn't used to do any calculations.
  //It's just used to trigger the function to get called.
  translate(redrawTrigger: any, key: any, ...params: Array<string>): any {

    //NOTE: This does not get called if this.redrawTrigger == null.
    //A lot of times the issue is that the component does not call super.ready() in its overridden ready function.
    let args = {};
    if (params != null && params.length > 0) {
      for (let i = 0; i < params.length; i++) {
        args['arg' + i] = params[i];
      }
    }
    return pdsI18n.getText(key, args);
  }

  /**
   * Force the polymer component to redraw anything that references the object or array at 'path', which should follow polymer path notation.
   *  Example: this.options is an array used in a dom-repeat to display a list of checkboxes. redraw('options') will redraw the list of checkboxes.
   *  See https://polymer-library.polymer-project.org/1.0/docs/devguide/model-data for details on the path notation pattern and overriding dirty checking.
   */
  redraw(path: string): void {
    let opts = this[path];
    this[path] = null;
    this[path] = opts;
  }

  /**
   * How many swear words have you said trying to get Polymer to redraw an array when just one of the properties in the
   * array were mutated?  If the answer is "LOTS" then this function is for you.
   *
   * This function will force polymer to update the DOM when you have mutated one of the properties of the object.
   *
   * If you pass in the target, it will do it only for that target, otherwise it will do it for all of the elements in
   * the array (which executes slower).
   *
   * @param pathOfArray (required) something like 'items' or 'wizard.items'
   * @param target (optional) The target object to update. This MUST be one of the items in the array.
   *                          If you pass nothing in, then this function will update ALL of the objects.
   * @param mutatedProperties (optional) An array of property names on the object to update.
   *                                     Defaults to all of the properties of the object.
   */
  redrawArray(pathOfArray: string, target?: Object | undefined, mutatedProperties?: Array<string>): void {

    let resolvedToken: any = this;
    for (let token of pathOfArray.split('.')) {
      resolvedToken = resolvedToken[token];
    }

    if (resolvedToken instanceof Array) {
      let arrayToUpdate: Array<any> = resolvedToken;

      let startIndex = 0;
      let endIndex: number = arrayToUpdate.length;

      if (target != null) {
        //We have a target, just tell polymer to update for this specific Array item.
        if (mutatedProperties == null) {
          mutatedProperties = Object.keys(target);
        }

        //If the target is specified, then we only want to loop one time in the outer loop.
        let index = arrayToUpdate.indexOf(target);
        if (index < 0) {
          return;
        }
        for (let prop of mutatedProperties) {
          //This is where the magic happens.  We tell polymer to go update the DOM for that array element.
          this.notifyPath(pathOfArray + '.' + index + '.' + prop);
        }
      } else if (arrayToUpdate.length > 0) {
        //We don't have a target.  Let's tell polymer to update for all the items in the array!
        if (mutatedProperties == null) {
          mutatedProperties = Object.keys(arrayToUpdate[0]);
        }

        //Loop on all of the objects in the array
        for (let i = 0; i < arrayToUpdate.length; i++) {
          for (let prop of mutatedProperties) {
            //This is where the magic happens.  We tell polymer to go update the DOM for that array element.
            this.notifyPath(pathOfArray + '.' + i + '.' + prop);
          }
        }
      }
    }
  }

  /**
   * In polymer we often bind an object to a button or link.  We then need to extract that object
   * out of the button.  But the event may have come from clicking on an icon inside the button. This method
   * will traverse up the DOM until it finds the button, and then gets the contents of the property and
   * returns it.
   * @param event - The event that came from the click event.
   * @param tagWithProperty - The tag name that has the object bound to it.  Most likely 'a' or 'button'.
   * @param property - The property name that you bound to the element.
   * @returns {any} - Any object that you bound to the element.
   */
  extractProperty(event: Event, tagWithProperty: string, property: string): any | undefined {
    let element = this.extractElement(event, tagWithProperty);
    if (element != null && property != null) {
      return element[property];
    }
  }
  /**
   * In polymer we often set an attribute on a button or link.  We then need to extract that attribute
   * from the button.  But the event may have come from clicking on an icon inside the button. This method
   * will traverse up the DOM until it finds the button, and then gets the contents of the attribute and
   * returns it.
   * @param event - The event that came from the click event.
   * @param tagWithProperty - The tag name that has the object bound to it.  Most likely 'a' or 'button'.
   * @param attribute - The attribute name that you set on the element.
   * @returns {string | undefined} - the attribute, or undefined if it does not exist.
   */
  extractAttribute(event: Event, tagWithAttribute: string, attribute: string): string | undefined {
    let element = this.extractElement(event, tagWithAttribute);
    if (element != null && attribute != null) {
      return element.getAttribute(attribute) as any as string;
    }
  }

  /**
   * It is possible to create read-only properties on a polymer component so that they cannot be changed by the outside world.
   * See https://polymer-library.polymer-project.org/1.0/docs/devguide/properties#read-only
   * This allows for default value of null to set on them if they end up needing to be accessed in the template which is pretty nice.
   * However, to set the property you have to use the format this._setMyVariableName(value) which is a pain because TypeScript won't
   * recognize it.  This function allows us to set the private property without needing to fight typescript.
   * Example this.setPrivatePolymerPropertyValue('myVariableName', 'hello world');
   */
  setPrivatePolymerPropertyValue(property: string, value: any): void {
    let privateSetterName: string = '_set' + property.charAt(0).toUpperCase() + property.slice(1);
    if (typeof this[privateSetterName] === 'function') {
      this[privateSetterName](value);
    } else {
      //The private setter function isn't available when the unit tests run so we just need to set the property right
      //on the object.
      this[property] = value;
    }
  }

  /**
   * Polymer does not automatically detect changes to sub properties. This function will create a PropertyObserver for a given
   * subproperty, which will notify polymer when it detects changes to the specified sub property.
   * See https://polymer-library.polymer-project.org/1.0/docs/devguide/data-system#observable-changes for details on why this is necessary
   */
  watch(path: string): PropertyObserver {
    //Define it if it hasn't been used yet.
    if (this.observeredPaths == null) {
      this.observeredPaths = {};
    }
    // if an observer already exists for the path, recreate it
    if (this.observeredPaths[path] != null) {
      this.observeredPaths[path].disable();
      delete this.observeredPaths[path];
    }

    // create the property observer
    let observer = new PropertyObserver(this, path);

    // turn on the property observer
    observer.enable();

    // keep track of the new observer so it can be destroyed on detach
    this.observeredPaths[path] = observer;

    // return the observer so the invoker can destroy it as they please
    return observer;
  }

  cancelSubscriptions(): void {
    for (let subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.subscriptions.length = 0;
  }

  //used by both extractProperty and extractAttribute
  private extractElement(event: Event, tagWithProperty: string): HTMLElement | undefined {
    if (event.target == null || tagWithProperty == null) {
      return;
    }
    let targetElement: HTMLElement = event.target as HTMLElement;
    if (!pdsUtils.is(event.target as HTMLElement, tagWithProperty)) {
      targetElement = pdsUtils.getClosest(event.target as HTMLElement, tagWithProperty) as HTMLElement;
      if (targetElement == null) {
        return;
      }
    }
    return targetElement;
  }
}
